import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useUpdatePdfTemplateSettings } from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./pdf-template-settings.i18n";

type PdfTemplate = {
  id: string;
  name: string;
  description: string | null;
  flowId: string;
  isActive: boolean;
  includeProductDocuments: boolean;
  flow: {
    id: string;
    name: string;
  };
};

type PdfTemplateSettingsProps = {
  template: PdfTemplate;
};

export function PdfTemplateSettings({ template }: PdfTemplateSettingsProps) {
  const [error, setError] = useState<string | null>(null);
  const updateSettingsMutation = useUpdatePdfTemplateSettings();
  const t = useT(T);
  const [formData, setFormData] = useState({
    name: template.name,
    description: template.description || "",
    isActive: template.isActive,
    includeProductDocuments: template.includeProductDocuments,
  });

  const isPending = updateSettingsMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await updateSettingsMutation.mutateAsync({
        id: template.id,
        data: {
          name: formData.name,
          description: formData.description || null,
          isActive: formData.isActive,
          includeProductDocuments: formData.includeProductDocuments,
        },
      });
      toast.success(t.saveChanges);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSave);
    }
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
        )}

        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              {t.name} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t.namePlaceholder}
              required
              disabled={isPending}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t.description}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t.descriptionPlaceholder}
              rows={3}
              disabled={isPending}
            />
          </div>

          {/* Flow (Read-only) - keeping "Flow" untranslated as it's a domain term */}
          <div className="space-y-2">
            <Label>{t.flowLabel}</Label>
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm font-medium">{template.flow.name}</p>
            </div>
          </div>

          {/* Is Active */}
          <div className="flex items-center justify-between space-x-2 p-4 border rounded-md">
            <div className="space-y-0.5">
              <Label htmlFor="isActive" className="cursor-pointer">
                {t.status}
              </Label>
              <p className="text-sm text-muted-foreground">
                {formData.isActive ? t.active : t.inactive}
              </p>
            </div>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              disabled={isPending}
            />
          </div>

          {/* Include product documents */}
          <div className="flex items-center justify-between space-x-2 p-4 border rounded-md">
            <div className="space-y-0.5">
              <Label htmlFor="includeProductDocuments" className="cursor-pointer">
                {t.includeProductDocuments}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t.includeProductDocumentsDescription}
              </p>
            </div>
            <Switch
              id="includeProductDocuments"
              checked={formData.includeProductDocuments}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, includeProductDocuments: checked })
              }
              disabled={isPending}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" loading={isPending}>
            {t.save}
          </Button>
        </div>
      </form>
    </Card>
  );
}
